import { useState } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const DAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MON = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const todayIso = () => iso(new Date());
export const longDate = (s) => { if (!s) return "—"; const d = parse(s); return `${DAY[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()}`; };

// Monday-start weeks, same convention as the KPI period selector.
export function auditRange(kind, custom) {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  if (kind === "today") return { start: iso(t), end: iso(t) };
  if (kind === "yesterday") { const y = new Date(t); y.setDate(t.getDate() - 1); return { start: iso(y), end: iso(y) }; }
  if (kind === "week" || kind === "last_week") {
    const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() || 7) - 1) - (kind === "last_week" ? 7 : 0));
    const e = new Date(s); e.setDate(s.getDate() + 6);
    return { start: iso(s), end: iso(e) };
  }
  if (kind === "day" && custom.start) return { start: custom.start, end: custom.start };
  if (kind === "custom" && custom.start && custom.end) return { start: custom.start, end: custom.end };
  return null;
}

const CAL = "cal-white";

const OPTS = [
  { v: "recent", label: "Most recent work day" },
  { v: "today", label: "Current day" },
  { v: "yesterday", label: "Yesterday" },
  { v: "week", label: "Current week" },
  { v: "last_week", label: "Last week" },
  { v: "custom", label: "Custom date range" },
];

export function AuditPeriodBar({ period, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ start: period.start || "", end: period.end || "" });
  const pick = (v) => (v === "custom" ? setOpen(true) : onChange({ kind: v }));
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="audit-period-bar">
      {OPTS.map((o) => (
        <button key={o.v} onClick={() => pick(o.v)} data-testid={`audit-period-${o.v}`}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${period.kind === o.v ? "bg-[#327de3] text-white border-[#327de3]" : "bg-white text-slate-600 border-slate-200 hover:border-[#327de3] hover:text-[#1f64c2]"}`}>
          {o.label}
        </button>
      ))}
      <Input type="date" value={period.kind === "day" ? period.start : ""} onChange={(e) => e.target.value && onChange({ kind: "day", start: e.target.value })}
        className={`h-8 w-40 ${CAL} ${period.kind === "day" ? "border-[#327de3]" : ""}`} title="Pick a single day" data-testid="audit-date-picker" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="audit-custom-dialog">
          <DialogHeader><DialogTitle>Entry audit date range</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Start date</Label><Input type="date" value={draft.start} onChange={(e) => setDraft({ ...draft, start: e.target.value })} className={CAL} data-testid="audit-custom-start" /></div>
            <div><Label className="text-xs">End date</Label><Input type="date" value={draft.end} onChange={(e) => setDraft({ ...draft, end: e.target.value })} className={CAL} data-testid="audit-custom-end" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="audit-custom-cancel">Cancel</Button>
            <Button disabled={!draft.start || !draft.end} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="audit-custom-apply"
              onClick={() => { const [s, e] = [draft.start, draft.end].sort(); onChange({ kind: "custom", start: s, end: e }); setOpen(false); }}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AuditDuplicates({ data }) {
  const dups = data.range_duplicates || [];
  const byDate = dups.reduce((m, d) => ((m[d.date] = m[d.date] || []).push(d), m), {});
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="audit-duplicates">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Duplicate entries{data.is_range ? " · selected period" : ""}</div>
        <span className="text-xs font-bold text-amber-700 bg-white px-2 py-0.5 rounded-full" data-testid="audit-duplicates-count">{dups.length}</span>
      </div>
      {dups.length === 0 ? <div className="text-xs text-slate-500 italic" data-testid="audit-no-duplicates">No duplicate entries found.</div> : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {Object.keys(byDate).sort().reverse().map((d) => (
            <div key={d} data-testid={`audit-dup-day-${d}`}>
              <div className="text-[11px] font-bold text-slate-700 mb-1">{longDate(d)} <span className="text-slate-400 font-normal">· {byDate[d].length}</span></div>
              <ul className="text-xs text-slate-800 space-y-1">
                {byDate[d].map((x) => (
                  <li key={`${x.employee}|${x.date}`} className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1.5 border border-amber-200">
                    <span className="font-semibold">{x.employee}</span><span className="text-slate-500">{x.date}</span>
                    <span className="text-amber-700 font-bold tabular-nums">×{x.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DayStatus({ day, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = open ? CaretDown : CaretRight;
  return (
    <div className="border-b border-slate-100 last:border-b-0 py-1.5" data-testid={`audit-day-${day.date}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left" data-testid={`audit-day-toggle-${day.date}`}>
        <span className="text-xs font-bold text-slate-800 flex items-center gap-1"><Icon size={11} />{longDate(day.date)}{day.date === todayIso() && <span className="text-[10px] font-normal text-slate-400 ml-1">· today, in progress</span>}</span>
        <span className="text-[11px] font-semibold"><span className={day.missing.length ? "text-rose-600" : "text-slate-400"}>{day.missing.length} missing</span><span className="text-slate-300 mx-1">·</span><span className="text-emerald-700">{day.submitted.length} entered</span></span>
      </button>
      {open && (
        <div className="mt-1 pl-4 space-y-0.5 text-xs leading-snug">
          <div><span className="text-slate-500">Missing: </span>{day.missing.length ? <span className="text-rose-600 font-semibold">{day.missing.join(", ")}</span> : <span className="text-slate-400 italic">none</span>}</div>
          <div><span className="text-slate-500">Entered: </span>{day.submitted.length ? <span className="text-emerald-700">{day.submitted.join(", ")}</span> : <span className="text-slate-400 italic">none</span>}</div>
        </div>
      )}
    </div>
  );
}

export function AuditSubmissions({ data }) {
  const days = data.days || [];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="audit-missing">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Submission status</div>
        <span className="text-xs font-bold text-rose-600" data-testid="audit-total-missing">{data.total_missing} missing{data.is_range ? " in period" : ""}</span>
      </div>
      {days.length === 0 ? <div className="text-xs text-slate-500 italic" data-testid="audit-no-workdays">No work days (Mon–Sat, up to today) in this period.</div> : (
        <div className="max-h-80 overflow-y-auto" data-testid="audit-people-list">
          {days.map((d) => <DayStatus key={d.date} day={d} defaultOpen={days.length <= 2 || d.missing.length > 0} />)}
        </div>
      )}
      <div className="text-[10px] text-slate-400 mt-2 flex items-center gap-3">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-rose-500" /> Missing</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> Entered</span>
      </div>
    </div>
  );
}
